/**
 * Field system additions (DS foundation): NumberField (units, step keys, grouping) and PhoneField (US formatting
 * as you type, international kept as typed). Both are the integrated-label TextField underneath, so label, hint,
 * error, optional marker, units and the sm/md height scale behave exactly like every other field.
 */
import { forwardRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { TextAreaField, TextField, type TextAreaFieldProps, type TextFieldProps } from "./controls.js";

/** The multi-line field. Same component as TextAreaField (kept for existing imports). */
export const TextArea = TextAreaField;
export type TextAreaProps = TextAreaFieldProps;

/* ------------------------------------------------------------------ numbers */

/** Parse what someone typed: "1,200", " 48.5 ", "$2,500" -> number; "" -> null; junk -> NaN. */
export function parseNumber(text: string): number | null {
  const t = text.replace(/[\s,$]/g, "");
  if (t === "" || t === "-" || t === ".") return null;
  return /^-?\d*\.?\d*$/.test(t) ? Number(t) : Number.NaN;
}

/** Clamp to [min, max] and round to `precision` decimals (floating error never leaks into the field). */
export function clampNumber(n: number, { min, max, precision }: { min?: number; max?: number; precision?: number }): number {
  let v = n;
  if (min !== undefined) v = Math.max(min, v);
  if (max !== undefined) v = Math.min(max, v);
  if (precision !== undefined) v = Number(v.toFixed(precision));
  return v;
}

export function formatNumber(n: number, precision?: number): string {
  return new Intl.NumberFormat("en-US", precision === undefined ? { maximumFractionDigits: 6 } : { minimumFractionDigits: 0, maximumFractionDigits: precision }).format(n);
}

export type NumberFieldProps = Omit<TextFieldProps, "value" | "onChange" | "defaultValue" | "type" | "suffix" | "min" | "max" | "step"> & {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Unit shown at the end of the box (lb, in, ft³, %). Announced with the value. */
  unit?: ReactNode;
  /** Spoken unit for the value text when `unit` is a symbol ("pounds" for "lb"). Defaults to `unit` when it is a string. */
  unitLabel?: string;
  min?: number;
  max?: number;
  /** ArrowUp/ArrowDown change the value by `step` (Shift: x10). Default 1. */
  step?: number;
  /** Decimal places kept (default: whatever is typed, up to 6). */
  precision?: number;
};

/**
 * Number input with units. A text input with `inputMode="decimal"` (no browser spinners, no scroll-wheel
 * accidents) and the spinbutton role: ArrowUp/ArrowDown step, Shift steps x10, Home/End jump to min/max.
 * Shows grouping ("1,200") when not focused; the raw digits while editing.
 */
export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField(
  { value, onChange, unit, unitLabel, min, max, step = 1, precision, onBlur, onFocus, onKeyDown, ...rest },
  ref,
) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value === null || Number.isNaN(value) ? "" : formatNumber(value, precision));
  const spoken = unitLabel ?? (typeof unit === "string" ? unit : "");
  const commit = (n: number | null) => onChange(n === null || Number.isNaN(n) ? n : clampNumber(n, { min, max, precision }));
  const bump = (e: KeyboardEvent<HTMLInputElement>, delta: number) => {
    e.preventDefault();
    const base = parseNumber(draft ?? String(value ?? "")) ?? min ?? 0;
    const next = clampNumber((Number.isNaN(base) ? 0 : base) + delta, { min, max, precision: precision ?? decimals(step) });
    setDraft(draft === null ? null : String(next));
    onChange(next);
  };
  return (
    <TextField
      {...rest}
      ref={ref}
      type="text"
      inputMode={precision === 0 ? "numeric" : "decimal"}
      role="spinbutton"
      aria-valuenow={value ?? undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={value === null ? undefined : `${formatNumber(value, precision)}${spoken ? ` ${spoken}` : ""}`}
      autoComplete="off"
      suffix={unit}
      value={shown}
      onFocus={(e) => {
        setDraft(value === null || Number.isNaN(value) ? "" : String(value));
        onFocus?.(e);
      }}
      onChange={(e) => {
        const t = e.target.value;
        setDraft(t);
        commit(parseNumber(t));
      }}
      onBlur={(e) => {
        setDraft(null);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === "ArrowUp") bump(e, step * (e.shiftKey ? 10 : 1));
        else if (e.key === "ArrowDown") bump(e, -step * (e.shiftKey ? 10 : 1));
        else if (e.key === "Home" && min !== undefined) { e.preventDefault(); setDraft(String(min)); onChange(min); }
        else if (e.key === "End" && max !== undefined) { e.preventDefault(); setDraft(String(max)); onChange(max); }
      }}
    />
  );
});
const decimals = (n: number) => (String(n).split(".")[1] ?? "").length;

/* ------------------------------------------------------------------ phone */

/**
 * Format a phone number as it is typed. US/Canada numbers (10 digits, optional leading 1) read "(732) 555-0100";
 * an extension ("x12", "ext 12") is kept as " x12". Anything starting with "+" other than +1 is international and
 * kept as typed (digits, spaces and dashes only).
 */
export function formatPhone(raw: string): string {
  const trimmed = raw.trimStart();
  const ext = /(?:\s*(?:x|ext\.?|extension)\s*)(\d{1,6})\s*$/i.exec(trimmed);
  const main = ext ? trimmed.slice(0, ext.index) : trimmed;
  const suffix = ext ? ` x${ext[1]}` : "";
  if (main.startsWith("+") && !/^\+\s*1/.test(main)) return `+${main.slice(1).replace(/[^\d\s-]/g, "")}${suffix}`;
  let d = main.replace(/\D/g, "");
  const intl = main.startsWith("+");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  else if (intl && d.startsWith("1")) d = d.slice(1);
  if (d.length > 10) return `${d}${suffix}`; // not a NANP number: keep the digits
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}${suffix}`;
}

/** Digits a phone value holds (for validation): "(732) 555-0100" -> "7325550100". */
export const phoneDigits = (v: string) => v.replace(/\D/g, "");

export type PhoneFieldProps = Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value: string;
  onChange: (value: string) => void;
};

/** Phone input: tel keyboard, autofill token, US formatting as you type; deleting through a ")" or "-" works. */
export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(function PhoneField({ value, onChange, autoComplete = "tel", ...rest }, ref) {
  return (
    <TextField
      {...rest}
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        // Backspacing over a separator would re-add it: when the digits didn't change, drop the last digit too.
        const deleting = next.length < value.length && phoneDigits(next) === phoneDigits(value);
        onChange(deleting ? formatPhone(phoneDigits(next).slice(0, -1)) : formatPhone(next));
      }}
    />
  );
});
